import { NUREAD_GW_URL, getGatewayToken, invalidateServerToken, getServerToken } from '$lib/auth.svelte'
import { requestServer } from "$lib/servers.svelte";
import axios from 'axios'

const client = new axios.Axios({
  baseURL: NUREAD_GW_URL
})
export async function getServers() {
  try {
    const gatewayToken = await getGatewayToken()
    const res = await client.get(
      "/servers",
      {
        // withCredentials: true,
        headers: {
          "Authorization": `Bearer ${gatewayToken}`,
          "Content-Type": "application/json"
        }

      })
    if (res.status == 401) {
      throw res.statusText
    }
    return JSON.parse(res.data)
  } catch (error) {
    return
  }
}

/** Claim ownership of an unclaimed server via gateway POST /servers/claim. */
export async function claimServer(claimToken: string, serverName: string) {
  const gatewayToken = await getGatewayToken()
  const res = await client.post(
    '/servers/claim',
    JSON.stringify({ claimToken, serverName }),
    {
      headers: {
        Authorization: `Bearer ${gatewayToken}`,
        'Content-Type': 'application/json',
      },
    }
  )
  if (res.status === 401) {
    throw new Error('unauthenticated')
  }
  if (res.status >= 400) {
    const detail =
      typeof res.data === 'string'
        ? (() => {
            try {
              return JSON.parse(res.data)?.detail
            } catch {
              return undefined
            }
          })()
        : (res.data as { detail?: string })?.detail
    throw new Error(detail || 'Claim token invalid or server already owned')
  }
  return typeof res.data === 'string' && res.data
    ? JSON.parse(res.data)
    : res.data
}

function errorDetail(data: unknown, fallback: string): string {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data)?.detail || fallback
    } catch {
      return fallback
    }
  }
  return (data as { detail?: string })?.detail || fallback
}

export type ServerUser = {
  userId: string
  username: string
  email: string
  roles: string[]
}

export type ServerSettings = {
  uid: string
  claimed: boolean
  users: ServerUser[]
}

/** Deployment settings: claim status + users (ADMIN/OWNER). */
export async function getServerSettings(server: any): Promise<ServerSettings> {
  const { data, status } = await requestServer(server, 'get', 'api/v1/server', null)
  if (status === 401) throw new Error('unauthenticated')
  if (status === 403) throw new Error('forbidden')
  if (status >= 400) throw new Error('Failed to load server settings')
  return data as ServerSettings
}

/** Rename a claimed server (gateway PATCH /servers/{uid}). Owner-only. */
export async function renameServer(serverUid: string, name: string): Promise<string> {
  const gatewayToken = await getGatewayToken()
  if (!gatewayToken) throw new Error('unauthenticated')
  const res = await client.patch(
    `/servers/${serverUid}`,
    JSON.stringify({ name }),
    {
      headers: {
        Authorization: `Bearer ${gatewayToken}`,
        'Content-Type': 'application/json',
      },
    },
  )
  if (res.status === 401) throw new Error('unauthenticated')
  if (res.status >= 400) {
    throw new Error(errorDetail(res.data, 'Failed to rename server'))
  }
  const body =
    typeof res.data === 'string' && res.data
      ? JSON.parse(res.data)
      : res.data
  return (body as { name?: string })?.name?.trim() || name.trim()
}

/** Disable/unregister a claimed server (gateway DELETE /servers/{uid}). Owner-only. */
export async function deleteServer(serverUid: string): Promise<void> {
  const gatewayToken = await getGatewayToken()
  if (!gatewayToken) throw new Error('unauthenticated')
  const res = await client.delete(`/servers/${serverUid}`, {
    headers: {
      Authorization: `Bearer ${gatewayToken}`,
      'Content-Type': 'application/json',
    },
  })
  if (res.status === 401) throw new Error('unauthenticated')
  if (res.status >= 400) {
    throw new Error(errorDetail(res.data, 'Failed to delete server'))
  }
}

/** Re-push gateway member add actions to the deployment queue. Owner-only; insert-only on server. */
export async function syncServerUsers(serverUid: string): Promise<number> {
  const gatewayToken = await getGatewayToken()
  if (!gatewayToken) throw new Error('unauthenticated')
  const res = await client.post(`/servers/${serverUid}/users/sync`, null, {
    headers: {
      Authorization: `Bearer ${gatewayToken}`,
      'Content-Type': 'application/json',
    },
  })
  if (res.status === 401) throw new Error('unauthenticated')
  if (res.status >= 400) {
    throw new Error(errorDetail(res.data, 'Failed to sync users'))
  }
  const body =
    typeof res.data === 'string' && res.data
      ? JSON.parse(res.data)
      : res.data
  return Number((body as { pushed?: number })?.pushed ?? 0)
}

/** Change a user's local role to ADMIN or MEMBER (ADMIN/OWNER). */
export async function setServerUserRole(
  server: any,
  userId: string,
  role: 'ADMIN' | 'MEMBER',
): Promise<void> {
  const { status, data } = await requestServer(
    server,
    'patch',
    `api/v1/server/users/${encodeURIComponent(userId)}`,
    { role },
  )
  if (status === 401) throw new Error('unauthenticated')
  if (status === 403) {
    throw new Error(
      typeof data?.detail === 'string' ? data.detail : 'Forbidden',
    )
  }
  if (status >= 400) {
    throw new Error(
      typeof data?.detail === 'string' ? data.detail : 'Failed to update role',
    )
  }
}

export type BackupFileInfo = {
  name: string
  size: number
  createdAt: string
}

export type BackupSettings = {
  enabled: boolean
  intervalHours: number
  lastSuccessAt?: string | null
  lastAttemptAt?: string | null
  lastError?: string
  backups: BackupFileInfo[]
}

export type BackupIntervalHours = 6 | 12 | 24 | 168

export type TranslationCacheMaxBytes =
  | 1073741824
  | 5368709120
  | 10737418240
  | 21474836480
  | 53687091200

export type TranslationCacheRetentionHours = 24 | 72 | 168 | 336 | 720

/** MIT translator backend; `custom_openai` is branded as Ollama in the UI. */
export type TranslationTranslator = 'deepseek' | 'custom_openai'

export type TranslationCacheSettings = {
  enabled: boolean
  maxBytes: number
  retentionHours: number
  mitApiUrl: string
  translator: TranslationTranslator
  eligibleLanguages: string[]
  usedBytes: number
  entryCount: number
  lastPruneAt?: string | null
  lastPruneError?: string
}

export type MitReachabilityResult = {
  ok: boolean
  message: string
  latencyMs?: number
  checkedUrl?: string
}

/** Translation cache limits + usage (ADMIN/OWNER). */
export async function getTranslationCacheSettings(
  server: any,
): Promise<TranslationCacheSettings> {
  const { data, status } = await requestServer(
    server,
    'get',
    'api/v1/server/translation-cache',
    null,
  )
  if (status === 401) throw new Error('unauthenticated')
  if (status === 403) throw new Error('forbidden')
  if (status >= 400) {
    throw new Error(
      typeof data?.detail === 'string'
        ? data.detail
        : 'Failed to load translation cache settings',
    )
  }
  return data as TranslationCacheSettings
}

/** Update translation cache limits (ADMIN/OWNER). */
export async function updateTranslationCacheSettings(
  server: any,
  enabled: boolean,
  maxBytes: TranslationCacheMaxBytes,
  retentionHours: TranslationCacheRetentionHours,
  mitApiUrl: string,
  translator: TranslationTranslator,
  eligibleLanguages: string[],
): Promise<TranslationCacheSettings> {
  const { data, status } = await requestServer(
    server,
    'patch',
    'api/v1/server/translation-cache',
    { enabled, maxBytes, retentionHours, mitApiUrl, translator, eligibleLanguages },
  )
  if (status === 401) throw new Error('unauthenticated')
  if (status === 403) throw new Error('forbidden')
  if (status >= 400) {
    throw new Error(
      typeof data?.detail === 'string'
        ? data.detail
        : 'Failed to update translation cache settings',
    )
  }
  return data as TranslationCacheSettings
}

/** Test manga-image-translator reachability (ADMIN/OWNER). */
export async function testTranslationCacheMit(
  server: any,
  mitApiUrl?: string,
): Promise<MitReachabilityResult> {
  const { data, status } = await requestServer(
    server,
    'post',
    'api/v1/server/translation-cache/test',
    mitApiUrl ? { mitApiUrl } : {},
  )
  if (status === 401) throw new Error('unauthenticated')
  if (status === 403) throw new Error('forbidden')
  if (status >= 400) {
    throw new Error(
      typeof data?.detail === 'string'
        ? data.detail
        : 'Failed to test MIT server',
    )
  }
  return data as MitReachabilityResult
}

export type NovelTranslationBackend = 'deepseek' | 'anthropic' | 'ollama'

export type NovelTranslationSettings = {
  enabled: boolean
  backend: NovelTranslationBackend
  ollamaHost: string
  maxBytes: number
  retentionHours: number
  eligibleLanguages: string[]
  usedBytes: number
  entryCount: number
  lastPruneAt?: string | null
  lastPruneError?: string
}

export type NovelTranslationReachabilityResult = MitReachabilityResult

/** Novel translation settings + usage (ADMIN/OWNER). */
export async function getNovelTranslationSettings(
  server: any,
): Promise<NovelTranslationSettings> {
  const { data, status } = await requestServer(
    server,
    'get',
    'api/v1/server/novel-translation',
    null,
  )
  if (status === 401) throw new Error('unauthenticated')
  if (status === 403) throw new Error('forbidden')
  if (status >= 400) {
    throw new Error(
      typeof data?.detail === 'string'
        ? data.detail
        : 'Failed to load novel translation settings',
    )
  }
  return data as NovelTranslationSettings
}

/** Update novel translation settings (ADMIN/OWNER). */
export async function updateNovelTranslationSettings(
  server: any,
  enabled: boolean,
  backend: NovelTranslationBackend,
  ollamaHost: string,
  maxBytes: TranslationCacheMaxBytes,
  retentionHours: TranslationCacheRetentionHours,
  eligibleLanguages: string[],
): Promise<NovelTranslationSettings> {
  const { data, status } = await requestServer(
    server,
    'patch',
    'api/v1/server/novel-translation',
    { enabled, backend, ollamaHost, maxBytes, retentionHours, eligibleLanguages },
  )
  if (status === 401) throw new Error('unauthenticated')
  if (status === 403) throw new Error('forbidden')
  if (status >= 400) {
    throw new Error(
      typeof data?.detail === 'string'
        ? data.detail
        : 'Failed to update novel translation settings',
    )
  }
  return data as NovelTranslationSettings
}

/** Test novel translation backend credentials/reachability (ADMIN/OWNER). */
export async function testNovelTranslationBackend(
  server: any,
  backend?: NovelTranslationBackend,
  ollamaHost?: string,
): Promise<NovelTranslationReachabilityResult> {
  const body: { backend?: NovelTranslationBackend; ollamaHost?: string } = {}
  if (backend) body.backend = backend
  if (ollamaHost) body.ollamaHost = ollamaHost
  const { data, status } = await requestServer(
    server,
    'post',
    'api/v1/server/novel-translation/test',
    body,
  )
  if (status === 401) throw new Error('unauthenticated')
  if (status === 403) throw new Error('forbidden')
  if (status >= 400) {
    throw new Error(
      typeof data?.detail === 'string'
        ? data.detail
        : 'Failed to test novel translation backend',
    )
  }
  return data as NovelTranslationReachabilityResult
}

export type ImageEnhanceMaxBytes = TranslationCacheMaxBytes

export type ImageEnhanceRetentionHours = TranslationCacheRetentionHours

/** Enhance when min(width, height) is below this many pixels. */
export type ImageEnhanceMinShortEdge = 800 | 1000 | 1200 | 1500 | 1800 | 2000

export type ImageEnhanceSettings = {
  enabled: boolean
  maxBytes: number
  retentionHours: number
  enhanceApiUrl: string
  minShortEdge: number
  usedBytes: number
  entryCount: number
  lastPruneAt?: string | null
  lastPruneError?: string
}

export type EnhanceReachabilityResult = MitReachabilityResult

/** Image enhance settings + cache usage (ADMIN/OWNER). */
export async function getImageEnhanceSettings(
  server: any,
): Promise<ImageEnhanceSettings> {
  const { data, status } = await requestServer(
    server,
    'get',
    'api/v1/server/image-enhance',
    null,
  )
  if (status === 401) throw new Error('unauthenticated')
  if (status === 403) throw new Error('forbidden')
  if (status >= 400) {
    throw new Error(
      typeof data?.detail === 'string'
        ? data.detail
        : 'Failed to load image enhance settings',
    )
  }
  return data as ImageEnhanceSettings
}

/** Update image enhance settings (ADMIN/OWNER). */
export async function updateImageEnhanceSettings(
  server: any,
  enabled: boolean,
  maxBytes: ImageEnhanceMaxBytes,
  retentionHours: ImageEnhanceRetentionHours,
  enhanceApiUrl: string,
  minShortEdge: ImageEnhanceMinShortEdge,
): Promise<ImageEnhanceSettings> {
  const { data, status } = await requestServer(
    server,
    'patch',
    'api/v1/server/image-enhance',
    { enabled, maxBytes, retentionHours, enhanceApiUrl, minShortEdge },
  )
  if (status === 401) throw new Error('unauthenticated')
  if (status === 403) throw new Error('forbidden')
  if (status >= 400) {
    throw new Error(
      typeof data?.detail === 'string'
        ? data.detail
        : 'Failed to update image enhance settings',
    )
  }
  return data as ImageEnhanceSettings
}

/** Test page upscaler reachability (ADMIN/OWNER). */
export async function testImageEnhance(
  server: any,
  enhanceApiUrl?: string,
): Promise<EnhanceReachabilityResult> {
  const { data, status } = await requestServer(
    server,
    'post',
    'api/v1/server/image-enhance/test',
    enhanceApiUrl ? { enhanceApiUrl } : {},
  )
  if (status === 401) throw new Error('unauthenticated')
  if (status === 403) throw new Error('forbidden')
  if (status >= 400) {
    throw new Error(
      typeof data?.detail === 'string'
        ? data.detail
        : 'Failed to test upscaler',
    )
  }
  return data as EnhanceReachabilityResult
}

/** Backup schedule + file list (ADMIN/OWNER). */
export async function getBackupSettings(server: any): Promise<BackupSettings> {
  const { data, status } = await requestServer(
    server,
    'get',
    'api/v1/server/backup',
    null,
  )
  if (status === 401) throw new Error('unauthenticated')
  if (status === 403) throw new Error('forbidden')
  if (status >= 400) {
    throw new Error(
      typeof data?.detail === 'string' ? data.detail : 'Failed to load backup settings',
    )
  }
  return data as BackupSettings
}

/** Update backup schedule (ADMIN/OWNER). */
export async function updateBackupSettings(
  server: any,
  enabled: boolean,
  intervalHours: BackupIntervalHours,
): Promise<BackupSettings> {
  const { data, status } = await requestServer(
    server,
    'patch',
    'api/v1/server/backup',
    { enabled, intervalHours },
  )
  if (status === 401) throw new Error('unauthenticated')
  if (status === 403) throw new Error('forbidden')
  if (status >= 400) {
    throw new Error(
      typeof data?.detail === 'string' ? data.detail : 'Failed to update backup settings',
    )
  }
  return data as BackupSettings
}

/** Queue a manual database backup (ADMIN/OWNER). */
export async function triggerBackup(server: any): Promise<{ operation: any }> {
  const { data, status } = await requestServer(
    server,
    'post',
    'api/v1/server/backup',
    null,
  )
  if (status === 401) throw new Error('unauthenticated')
  if (status === 403) throw new Error('forbidden')
  if (status >= 400) {
    throw new Error(
      typeof data?.detail === 'string' ? data.detail : 'Failed to start backup',
    )
  }
  return data as { operation: any }
}

/** Delete a backup file by name (ADMIN/OWNER). */
export async function deleteBackup(server: any, name: string): Promise<void> {
  const { status, data } = await requestServer(
    server,
    'delete',
    `api/v1/server/backup/${encodeURIComponent(name)}`,
    null,
  )
  if (status === 401) throw new Error('unauthenticated')
  if (status === 403) throw new Error('forbidden')
  if (status >= 400) {
    throw new Error(
      typeof data?.detail === 'string' ? data.detail : 'Failed to delete backup',
    )
  }
}

/** Download a backup file; triggers a browser save dialog. */
export async function downloadBackup(server: any, name: string): Promise<void> {
  const token = await getServerToken(server.uid)
  if (!token) throw new Error('unauthenticated')
  const urls: string[] = server?.urls ?? []
  if (!urls.length) throw new Error('Server unreachable')

  let lastErr: unknown
  for (const baseURL of urls) {
    try {
      const res = await axios({
        method: 'get',
        url: `${baseURL}/api/v1/server/backup/${encodeURIComponent(name)}`,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      })
      if (res.status === 401) throw new Error('unauthenticated')
      if (res.status >= 400) {
        throw new Error('Failed to download backup')
      }
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data])
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href
      a.download = name
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(href)
      return
    } catch (err) {
      lastErr = err
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('Failed to download backup')
}

export async function readPath(server: any, path: string) {
  try {
    const { data, status } = await requestServer(
      server,
      'post',
      `api/v1/readpath`,
      {path},
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error: any) {
    if (error?.response?.status == 401) {
      invalidateServerToken(server.uid)
      return
    }
    console.log(error)
    return {}
  }
}
